/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Link, Stack } from '@fluentui/react';
import { observer } from 'mobx-react';
import React, { useEffect } from 'react';
import { useHistory } from 'react-router';
import { CluTrainedModelStore } from 'src/clu/core/stores/CluTrainedModelStore';
import { EmptyStateView } from 'src/core/components/EmptyStateView';
import { LuisText } from 'src/core/components/LuisText';
import { RBACTooltip } from 'src/core/components/RBACTooltip';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { logButtonClick } from 'src/core/services/telemetry/eventsNamesTemplate';
import { getScopedTheme } from 'src/core/themes/luisStylist';
import { mapUserRoleToUserActions } from 'src/core/utils/azureUtils';
import { getPortalFromUrl } from 'src/core/utils/routingUtils';
import { modelGuidanceCardKeyToName } from 'src/core/utils/telemetryUtils';
import { useModelGuidanceCardsData } from 'src/customText/core/hooks/useModelGuidanceCardsData';
import { CustomTextTrainedModelStore } from 'src/customText/core/stores/CustomTextTrainedModelStore';
import { CapabilityType } from 'src/languageStudio/core/types';
import { ModelEvaluationStore } from 'src/modules/modelDetails/stores/ModelEvaluationStore';
import { GuidanceDetailsBlade } from 'src/modules/modelGuidance/components/GuidanceDetailsBlade';
import { ModelDetailsCard } from 'src/modules/modelGuidance/components/ModelDetailsCard';
import { ModelGuidanceCard } from 'src/modules/modelGuidance/components/ModelGuidanceCard';
import { ModelGuidanceCardMessages } from 'src/modules/modelGuidance/components/modelGuidanceCardMessages';
import { getRouteToTraining, SchemaType } from 'src/modules/modelGuidance/modelGuidanceUtils/modelGuidanceUtils';
import { ModelGuidanceStore } from 'src/modules/modelGuidance/stores/ModelGuidanceStore';
import styled from 'styled-components';

type OverviewTabProps = {
    pivotName?: SchemaType;
    currentProjectName: string;
    isExtractionProject: boolean;
    capabilityName: CapabilityType;
    modelGuidanceStore?: ModelGuidanceStore;
    onRenderOptions?: () => React.ReactNode;
    modelEvaluationStore: ModelEvaluationStore;
    onLinkToOtherTabClick: (tabKey: string) => void;
    trainedModel: CustomTextTrainedModelStore | CluTrainedModelStore;
};

export type ModelGuidanceCardKey =
    | 'improve-model'
    | 'missing-labels-card'
    | 'not-enough-data-card'
    | 'confusion-labels-card'
    | 'unbalanced-labels-card'
    | 'uneven-distribution-card';

const theme = getScopedTheme('OverviewTab');

const GuidanceTitle = styled(LuisText)({
    fontWeight: 600,
    fontSize: '20px',
    lineHeight: '28px'
});

const GuidanceSubTitle = styled(LuisText)({
    fontWeight: 400,
    fontSize: '14px',
    lineHeight: '20px'
});

export const OverviewTab = observer((props: OverviewTabProps) => {
    const {
        trainedModel,
        currentProjectName,
        modelGuidanceStore,
        isExtractionProject,
        modelEvaluationStore,
        capabilityName,
        onLinkToOtherTabClick,
        onRenderOptions,
        pivotName = null
    } = props;

    const history = useHistory();
    const { serviceName } = getPortalFromUrl();
    const { localize } = useLocalization();
    const { rootStore } = useGlobalState();

    const isCluProject = capabilityName === 'Conversation' || capabilityName === 'Orchestration';
    const { id: projectId } = isCluProject ? rootStore.currentCluProjectStore : rootStore.currentCustomTextProjectStore;

    const { userRole, displayedUserRole } = rootStore.serviceKey;
    const { canWrite } = mapUserRoleToUserActions(userRole);

    const [isViewDetailsBladeOpen, setIsViewDetailsOpen] = React.useState(false);
    const [selectedDetailsKey, setSelectedDetailsKey] = React.useState<ModelGuidanceCardKey>(null);

    const [callingRef, setCallingRef] = React.useState<React.RefObject<HTMLAnchorElement>>(null);

    const handleViewDetailsClick = (key: ModelGuidanceCardKey) => {
        setSelectedDetailsKey(key);
        setIsViewDetailsOpen(true);
        logButtonClick(serviceName, 'ModelPerformance', modelGuidanceCardKeyToName(key), null, projectId);
    };

    useEffect(() => {
        if (!isViewDetailsBladeOpen && callingRef?.current) {
            callingRef.current?.focus();
        }
    }, [isViewDetailsBladeOpen]);

    const handleViewDetailsDismiss = () => {
        setSelectedDetailsKey(null);
        setIsViewDetailsOpen(false);
    };

    const { modelGuidanceCardArray } = useModelGuidanceCardsData({
        pivotName,
        modelGuidanceStore,
        isExtractionProject,
        handleViewDetailsClick
    });

    const routeToTraining = () => {
        history.push(getRouteToTraining(capabilityName, projectId));
    };

    const handleImproveModelLink = (linkRef: React.RefObject<HTMLAnchorElement>) => {
        setCallingRef(linkRef);
        logButtonClick(serviceName, 'ModelPerformance', 'ImproveModel', null, projectId);
        handleViewDetailsClick('improve-model');
    };

    const shouldNavigateToDataTagging = (filters: string[]) => {
        if (
            capabilityName !== 'Orchestration' ||
            (filters.length === 1 && !Boolean(rootStore.currentCluProjectStore?.intentStoreHouse.findById(filters[0]).orchestrationType))
        ) {
            return true;
        }

        return false;
    };

    const isRecommendationLinkDisabled = (itemsToFilter: string[], _pivotName: SchemaType) => {
        if (!isCluProject || itemsToFilter.length !== 1) {
            return false;
        }

        if (
            (_pivotName === 'Entities' && !Boolean(rootStore.currentCluProjectStore?.entityStoreHouse.findById(itemsToFilter[0]))) ||
            (_pivotName === 'Intents' && !Boolean(rootStore.currentCluProjectStore?.intentStoreHouse.findById(itemsToFilter[0])))
        ) {
            return true;
        }

        return false;
    };

    return (
        <Stack tokens={{ childrenGap: 24 }} styles={{ root: { margin: '20px 0 50px 0' } }} data-automation-id="overviewTab">
            {onRenderOptions && onRenderOptions()}

            <ModelDetailsCard
                pivotName={pivotName}
                trainModelInfo={trainedModel}
                modelEvaluationStore={modelEvaluationStore}
                onLearnMoreLinkClick={handleImproveModelLink}
                numberOfTestingFiles={modelGuidanceStore?.modelDistributionPerDataSet?.Test}
                numberOfTrainingFiles={modelGuidanceStore?.modelDistributionPerDataSet?.Train}
                fileTypeMessage={isCluProject ? localize(ModelGuidanceCardMessages.utterances) : undefined}
            />

            {modelGuidanceStore ? (
                <>
                    <Stack>
                        <GuidanceTitle as="h2">{localize(ModelGuidanceCardMessages.guidanceTitle)}</GuidanceTitle>
                        <GuidanceSubTitle>{localize(ModelGuidanceCardMessages.guidanceSubtitle)}</GuidanceSubTitle>
                    </Stack>

                    {modelGuidanceCardArray
                        .sort((a, b) => (a.numberOfIssues > b.numberOfIssues ? -1 : 1))
                        .map(
                            cardInfo =>
                                (pivotName === null ||
                                    (cardInfo.cardKey !== 'unbalanced-labels-card' && cardInfo.cardKey !== 'uneven-distribution-card')) && (
                                    <ModelGuidanceCard
                                        key={cardInfo.cardKey}
                                        errorTitle={cardInfo.errorMessage}
                                        rightTitle={cardInfo.rightMessage}
                                        numberOfIssues={cardInfo.numberOfIssues}
                                        errorDescription={cardInfo.errorDescription}
                                        issuesMessage={cardInfo.issuesNumberMessage}
                                        onViewDetailsClick={cardInfo.onViewDetailsClick}
                                    />
                                )
                        )}
                </>
            ) : (
                <EmptyStateView
                    message={
                        <>
                            {localize(ModelGuidanceCardMessages.noRecommendationAvailable1)}
                            &nbsp;
                            <RBACTooltip
                                hidden={canWrite}
                                displayedUserRole={displayedUserRole}
                                data-automation-id="trainModelLinkLanguageToolTip"
                                componentSurroundedByToolTip={
                                    <Link
                                        disabled={!canWrite}
                                        onClick={routeToTraining}
                                        styles={{ root: { color: theme.emptyStateLinkColor } }}>
                                        {localize(ModelGuidanceCardMessages.retrain)}
                                    </Link>
                                }
                            />
                            &nbsp;
                            {localize(ModelGuidanceCardMessages.noRecommendationAvailable2)}
                        </>
                    }
                />
            )}

            <GuidanceDetailsBlade
                pivotName={pivotName}
                isOpen={isViewDetailsBladeOpen}
                projectName={currentProjectName}
                guidanceKey={selectedDetailsKey}
                onDismiss={handleViewDetailsDismiss}
                modelGuidanceStore={modelGuidanceStore}
                isExtractionProject={isExtractionProject}
                onLinkToOtherTabClick={onLinkToOtherTabClick}
                shouldNavigateToDataTagging={shouldNavigateToDataTagging}
                isRecommendationLinkDisabled={isRecommendationLinkDisabled}
            />
        </Stack>
    );
});
